(() => {
  if (window.CharterhouseRecommendationEngine) return;

  const normalize = value => String(value || '')
    .replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹]/g, '')
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

  const hasAny = (value, terms) => {
    const text = normalize(value);
    return terms.some(term => text.includes(normalize(term)));
  };

  const unique = values => [...new Set(values.filter(Boolean))];

  const brandMatchers = [
    ['tmc', /\b(tmc|tropical marine centre|tropical marine center)\b/],
    ['red sea', /\bred sea\b/],
    ['aqua illumination', /\b(aqua illumination|ai prime)\b/],
    ['ecotech', /\b(ecotech|eco tech)\b/],
    ['oase', /\boase\b/],
    ['eheim', /\beheim\b/],
    ['fluval', /\bfluval\b/],
    ['juwel', /\bjuwel\b/],
    ['biorb', /\bbiorb\b/],
    ['seachem', /\bseachem\b/],
    ['sicce', /\bsicce\b/],
    ['tunze', /\btunze\b/],
    ['deltec', /\bdeltec\b/],
    ['d-d', /\b(d d|d-d|dd h2ocean)\b/],
    ['aqua one', /\baqua one\b/],
    ['aquaforest', /\baquaforest\b/],
    ['maxspect', /\bmaxspect\b/],
    ['reef factory', /\breef factory\b/],
    ['chihiros', /\bchihiros\b/],
    ['twinstar', /\btwinstar\b/],
    ['kessil', /\bkessil\b/],
    ['innovative marine', /\binnovative marine\b/]
  ];

  const habitatMatchers = {
    reptile: /\b(reptile|reptiles|terrarium|vivarium|lizard|gecko|chameleon|bearded dragon|snake)\b/,
    pond: /\b(pond|ponds|koi|water garden)\b/,
    aquarium: /\b(aquarium|aquariums|fish tank|reef|marine|saltwater|corals?|sps|lps)\b/
  };

  const waterMatchers = {
    saltwater: /\b(marine|reef|saltwater|salt water|corals?|red sea|sps|lps)\b/,
    freshwater: /\b(freshwater|fresh water|tropical|planted|biorb|live plants?|aquatic plants?)\b/
  };

  const brandedPartPattern = /\b(spares?|spare parts|replacement|impeller|rotor|shaft|o ring|seal|return pipe|pipe|fitting|adapter|connector|mount|bracket|controller|filter sock|suction cup)\b/;
  const mountingPattern = /\b(mount|mounting|arm|bracket|rail|adapter|slide|hanging kit|diffuser|shade|light stand)\b/;
  const lightingPattern = /\b(radion|led light|reef light|aquarium light|lighting|hydra|kessil)\b/;
  const livestockTypePattern = /\b(hard corals?|soft corals?|coral\+?|inverts?|live plants?|pond plants?|shrimp|snails?|invertebrates?)\b/;

  const brandFor = value => {
    const text = normalize(value);
    return brandMatchers.find(([, pattern]) => pattern.test(text))?.[0] || '';
  };

  const habitatsFor = value => {
    const text = normalize(value);
    return Object.entries(habitatMatchers)
      .filter(([, pattern]) => pattern.test(text))
      .map(([name]) => name);
  };

  const waterTypesFor = value => {
    const text = normalize(value);
    return Object.entries(waterMatchers)
      .filter(([, pattern]) => pattern.test(text))
      .map(([name]) => name);
  };

  const modelTokens = value => {
    const text = normalize(value);
    return unique([
      text.match(/\bxr\s?(15|30)w?\b/)?.[0]?.replace(/\s/g, ''),
      text.match(/\bg\d+\b/)?.[0],
      text.match(/\b(xxl|xl)\b/)?.[0]
    ]);
  };

  const familyFor = value => {
    const text = normalize(value);
    if (text.includes('radion')) return 'radion';
    if (text.includes('biorb')) return 'biorb';
    if (text.includes('ai prime')) return 'ai prime';
    if (text.includes('biomaster')) return 'biomaster';
    if (text.includes('reefer')) return 'reefer';
    return '';
  };

  const record = input => {
    const title = input?.title || '';
    const type = input?.type || input?.product_type || '';
    const vendor = input?.vendor || '';
    const tags = Array.isArray(input?.tags) ? input.tags.join('|') : input?.tags || '';
    const collections = Array.isArray(input?.collections) ? input.collections.join('|') : input?.collections || '';
    const text = normalize([title, type, vendor, tags, collections].join(' '));
    const primary = normalize([title, type, vendor].join(' '));
    return {
      id: input?.id || '',
      title,
      type,
      vendor,
      tags,
      collections,
      text,
      primary,
      brand: brandFor([vendor, title].join(' ')),
      family: familyFor([title, type].join(' ')),
      habitats: habitatsFor(text),
      waterTypes: waterTypesFor(text),
      models: modelTokens(title)
    };
  };

  const fromDataset = dataset => record({
    id: dataset?.productId,
    title: dataset?.productTitle,
    type: dataset?.productType,
    vendor: dataset?.productVendor,
    tags: dataset?.productTags,
    collections: dataset?.productCollections
  });

  const fromCard = card => record({
    id: card?.dataset?.recKey,
    title: card?.dataset?.recTitle,
    type: card?.dataset?.recType,
    vendor: card?.dataset?.recVendor,
    tags: card?.dataset?.recTags
  });

  const fromProduct = product => record(product || {});

  const overlaps = (left, right) => left.some(value => right.includes(value));

  const habitatCompatible = (source, candidate) => {
    if (!source.habitats.length || !candidate.habitats.length) return true;
    if (source.habitats.includes('reptile')) return candidate.habitats.includes('reptile');
    if (candidate.habitats.includes('reptile')) return false;
    if (source.habitats.includes('pond') && !source.habitats.includes('aquarium')) {
      return candidate.habitats.includes('pond');
    }
    if (candidate.habitats.includes('pond') && !candidate.habitats.includes('aquarium')) return false;
    return true;
  };

  const waterCompatible = (source, candidate) => {
    if (source.waterTypes.length !== 1 || candidate.waterTypes.length !== 1) return true;
    return source.waterTypes[0] === candidate.waterTypes[0];
  };

  const brandCompatible = (source, candidate) => {
    if (!brandedPartPattern.test(candidate.text)) return true;
    if (!source.brand || !candidate.brand) return true;
    return source.brand === candidate.brand;
  };

  const modelCompatible = (source, candidate) => {
    if (!source.models.length || !candidate.models.length) return true;
    const categories = [
      token => token.startsWith('xr'),
      token => token.startsWith('g'),
      token => ['xl', 'xxl'].includes(token)
    ];
    return categories.every(matches => {
      const sourceTokens = source.models.filter(matches);
      const candidateTokens = candidate.models.filter(matches);
      if (!sourceTokens.length || !candidateTokens.length) return true;
      return overlaps(sourceTokens, candidateTokens);
    });
  };

  const lightingAccessoryCompatible = (source, candidate) => {
    if (!lightingPattern.test(source.primary) || !mountingPattern.test(candidate.text)) return true;
    if (source.family && candidate.family) return source.family === candidate.family;
    if (source.brand && candidate.brand) return source.brand === candidate.brand;
    return source.text.includes('radion') && (candidate.text.includes('radion') || candidate.text.includes('rms'));
  };

  const isCompatible = (sourceInput, candidateInput) => {
    const source = sourceInput?.text ? sourceInput : record(sourceInput);
    const candidate = candidateInput?.text ? candidateInput : record(candidateInput);
    if (!candidate.title && !candidate.text) return false;
    return habitatCompatible(source, candidate)
      && waterCompatible(source, candidate)
      && brandCompatible(source, candidate)
      && modelCompatible(source, candidate)
      && lightingAccessoryCompatible(source, candidate);
  };

  const isLiveStock = value => {
    const item = value?.text ? value : record(value);
    const type = normalize(item.type);
    if (type === 'aquarium') return false;
    return type.endsWith('fish') || livestockTypePattern.test(type);
  };

  const topicWords = value => {
    const generic = new Set(['aquarium', 'aquariums', 'aquatic', 'marine', 'freshwater', 'guide', 'guides', 'product', 'products', 'complete', 'ultimate', 'choose', 'choosing', 'best', 'your', 'with', 'from', 'explained', 'understanding', 'setting', 'maintaining']);
    return new Set(normalize(value).split(' ').map(word => word.replace(/(ing|ers|es|s)$/, '')).filter(word => word.length >= 4 && !generic.has(word)));
  };

  window.CharterhouseRecommendationEngine = Object.freeze({
    version: 1,
    normalize,
    hasAny,
    brandFor,
    habitatsFor,
    waterTypesFor,
    modelTokens,
    familyFor,
    record,
    fromDataset,
    fromCard,
    fromProduct,
    isCompatible,
    isLiveStock,
    isBrandedPart: value => brandedPartPattern.test(value?.text || record(value).text),
    topicWords
  });
})();
